import { useCallback } from 'react'
import { db } from '../stores/db'
import usePrefStore from '../stores/prefs'
import moment from 'moment'
import type { Order, OrderLine, RetailQuotaResult, SkuQuotaDetail, KTMClientType } from '../types'
import { computeRetailRoleForCustomer } from './useRetailRoleForCustomer'

const KOT_MARK_PREFIX = 'QUOTAS_'

const KTM_CLIENT_TYPES: Record<KTMClientType, string> = {
   RET: 'ECR',
   ERE: 'ECF',
   INT: 'Interco',
}

export type KTMDeployOptions = {
   replaceExistingQty: boolean   // MAJ qté des SKUs communs
   addNewLines: boolean          // Ajout des SKUs du KTM absents du KOT
   removeAbsentLines: boolean    // Retrait des SKUs du KOT absents du KTM
}

/**
 * Fusionne les lignes d'un KTM dans les lignes d'un KOT existant, selon les options.
 * Retourne un nouveau tableau de lignes (ne modifie pas les inputs).
 * Si aucune option n'est active, renvoie les lignes du KOT inchangées (pas de modification).
 */
export function mergeKTMIntoKOTLines(kotLines: OrderLine[], ktmLines: OrderLine[], opts: KTMDeployOptions): OrderLine[] {
   const ktmMap = new Map(ktmLines.map(l => [l.sku, l]))
   const kotSkus = new Set(kotLines.map(l => l.sku))
   const result: OrderLine[] = []

   for (const kotLine of kotLines) {
      const ktmLine = ktmMap.get(kotLine.sku)
      if (ktmLine) {
         // SKU présent des 2 côtés
         if (opts.replaceExistingQty) {
            result.push({ ...kotLine, qty: ktmLine.qty })
         } else {
            result.push(kotLine)
         }
      } else {
         // SKU présent uniquement dans le KOT
         if (!opts.removeAbsentLines) {
            result.push(kotLine)
         }
         // sinon : drop
      }
   }

   if (opts.addNewLines) {
      for (const ktmLine of ktmLines) {
         if (!kotSkus.has(ktmLine.sku)) {
            result.push({ sku: ktmLine.sku, qty: ktmLine.qty })
         }
      }
   }

   return result
}

const useRetailQuota = () => {

   /**
    * Génère la contremarque KOT pré-remplie avec le mois courant
    */
   const generateKOTMark = useCallback((): string => {
      return `${KOT_MARK_PREFIX}${moment().format('YYYYMM')}`
   }, [])

   /**
    * Extrait le YYYYMM d'une contremarque KOT
    */
   const extractPeriodFromMark = useCallback((mark: string | null): string | null => {
      if (!mark?.startsWith(KOT_MARK_PREFIX)) return null
      return mark.substring(KOT_MARK_PREFIX.length)
   }, [])

   /**
    * Cherche le KOT le plus récent (≤ mois courant) pour un client donné
    * dans orders + bi_orders
    */
   const findCurrentKOT = useCallback(async (customerId: string | null): Promise<{ kot: Order | null, hasAnyKOT: boolean }> => {
      if (!customerId) return { kot: null, hasAnyKOT: false }

      const currentPeriod = moment().format('YYYYMM')

      // Récupérer tous les KOT depuis orders et bi_orders
      const localKOTs = await db.orders
         .where('type').equals('KOT')
         .and(o => o.customer_id === customerId && o.status !== 'deleted')
         .toArray()
      const biKOTs = await db.bi_orders
         .where('type').equals('KOT')
         .and(o => o.customer_id === customerId)
         .toArray()

      // Fusionner et dédupliquer par ID (local prioritaire)
      const allMap = new Map<string, Order>()
      for (const o of biKOTs) {
         if (o.id) allMap.set(o.id, o)
      }
      for (const o of localKOTs) {
         if (o.id) allMap.set(o.id, o)  // Les locaux écrasent les BI
      }

      const hasAnyKOT = allMap.size > 0

      // Filtrer ceux dont la période ≤ mois courant
      let bestKOT: Order | null = null
      let bestPeriod = ''

      for (const kot of allMap.values()) {
         const period = extractPeriodFromMark(kot.mark)
         if (period && period <= currentPeriod && period > bestPeriod) {
            bestPeriod = period
            bestKOT = kot
         }
      }

      return { kot: bestKOT, hasAnyKOT }
   }, [extractPeriodFromMark])

   /**
    * Cherche un KOT d'une période donnée (YYYYMM) pour un client.
    * Regarde dans orders (local) + bi_orders (AppWrite). Le local écrase le BI en cas de doublon.
    * Retourne null si aucun KOT trouvé pour cette période.
    */
   const findKOTByPeriod = useCallback(async (customerId: string | null, period: string | null): Promise<Order | null> => {
      if (!customerId || !period) return null
      const expectedMark = `${KOT_MARK_PREFIX}${period}`

      const localKOT = await db.orders
         .where('type').equals('KOT')
         .and(o => o.customer_id === customerId && o.mark === expectedMark && o.status !== 'deleted')
         .first()
      if (localKOT) return localKOT

      const biKOT = await db.bi_orders
         .where('type').equals('KOT')
         .and(o => o.customer_id === customerId && o.mark === expectedMark)
         .first()
      return biKOT ?? null
   }, [])

   /**
    * Agrège les quantités consommées de toutes les ECR/ECF depuis une date YYYY-MM-01
    * Dédupliquées par ID entre orders et bi_orders
    */
   const getConsumedQuantities = useCallback(async (customerId: string | null, periodYYYYMM: string, excludeOrderId?: string | null): Promise<Map<string, number>> => {
      const consumed = new Map<string, number>()
      if (!customerId) return consumed

      const startDate = `${periodYYYYMM.substring(0, 4)}-${periodYYYYMM.substring(4, 6)}-01`

      // Récupérer ECR/ECF locales depuis la date
      const localOrders = await db.orders
         .where('customer_id').equals(customerId)
         .and(o => (o.type === 'ECR' || o.type === 'ECF') && o.date >= startDate && o.status !== 'deleted')
         .toArray()

      // Récupérer ECR/ECF BI depuis la date
      const biOrders = await db.bi_orders
         .where('customer_id').equals(customerId)
         .and(o => (o.type === 'ECR' || o.type === 'ECF') && o.date >= startDate)
         .toArray()

      // Dédupliquer par ID (local prioritaire)
      const allMap = new Map<string, Order>()
      for (const o of biOrders) {
         if (o.id) allMap.set(o.id, o)
      }
      for (const o of localOrders) {
         if (o.id) allMap.set(o.id, o)
      }

      // Exclure la commande en cours d'édition pour ne pas la compter dans la consommation
      if (excludeOrderId) {
         allMap.delete(excludeOrderId)
      }

      // Agréger les quantités par SKU
      for (const order of allMap.values()) {
         for (const line of order.lines) {
            const prev = consumed.get(line.sku) ?? 0
            consumed.set(line.sku, prev + line.qty)
         }
      }

      return consumed
   }, [])

   /**
    * Retourne le résultat complet du quota Retail pour un client
    */
   const getRetailQuota = useCallback(async (customerId?: string | null, excludeOrderId?: string | null): Promise<RetailQuotaResult> => {
      const cid = customerId ?? usePrefStore.getState().curCustomer
      const emptyResult: RetailQuotaResult = { kotOrder: null, period: null, skuQuotas: new Map(), hasAnyKOT: false }

      if (!cid) return emptyResult

      const { kot, hasAnyKOT } = await findCurrentKOT(cid)

      // Si aucun KOT n'existe pour ce client, pas de quota en place
      if (!hasAnyKOT) return emptyResult

      // Si des KOT existent mais aucun n'est applicable (tous dans le futur), quota = 0, ajout bloqué
      if (!kot) return { kotOrder: null, period: null, skuQuotas: new Map(), hasAnyKOT: true }

      const period = extractPeriodFromMark(kot.mark)
      if (!period) return emptyResult

      // Construire le budget depuis les lignes du KOT
      const budgets = new Map<string, number>()
      for (const line of kot.lines) {
         budgets.set(line.sku, (budgets.get(line.sku) ?? 0) + line.qty)
      }

      // Récupérer la consommation (en excluant la commande en cours d'édition)
      const consumed = await getConsumedQuantities(cid, period, excludeOrderId)

      // Construire le résultat
      const skuQuotas = new Map<string, SkuQuotaDetail>()

      // D'abord les SKUs budgétés
      for (const [sku, budgeted] of budgets) {
         const consumedQty = consumed.get(sku) ?? 0
         skuQuotas.set(sku, {
            budgeted,
            consumed: consumedQty,
            remaining: budgeted - consumedQty,
         })
      }

      // NB : on N'AJOUTE PAS les SKUs consommés mais NON budgétés. Un SKU absent du KOT actif a un
      // quota INDÉFINI (et non 0) → skuQuotas.get(sku) renvoie undefined → les écrans affichent un
      // tiret (et la validation ne le marque pas « over-quota »). Évite un « -20 » trompeur.

      return { kotOrder: kot, period, skuQuotas, hasAnyKOT: true }
   }, [extractPeriodFromMark, findCurrentKOT, getConsumedQuantities])

   /**
    * Retourne le restant pour un SKU (null si pas dans le quota / pas de KOT)
    */
   const getRemainingQtyForSku = useCallback(async (sku: string, customerId?: string | null, excludeOrderId?: string | null): Promise<number | null> => {
      const quota = await getRetailQuota(customerId, excludeOrderId)
      if (!quota.kotOrder) return null

      const detail = quota.skuQuotas.get(sku)
      if (!detail) return null
      return detail.remaining
   }, [getRetailQuota])

   /**
    * Restant d'un SKU à partir d'une map skuQuotas déjà calculée (pour l'affichage par ligne).
    * Renvoie null si le SKU est ABSENT du KOT actif → quota INDÉFINI (≠ 0). Source unique de la décision.
    */
   const getSkuRemaining = useCallback((skuQuotas: Map<string, SkuQuotaDetail>, sku: string): number | null => {
      return skuQuotas.get(sku)?.remaining ?? null
   }, [])

   /** Libellé d'affichage du restant : la valeur, ou un tiret cadratin « — » si quota indéfini. */
   const formatSkuRemaining = useCallback((skuQuotas: Map<string, SkuQuotaDetail>, sku: string): string => {
      const r = getSkuRemaining(skuQuotas, sku)
      return r === null ? '—' : String(r)
   }, [getSkuRemaining])

   /**
    * True si le user peut dépasser le quota du customer donné (admin global,
    * OU N1/N2 du customer). Si customerId est omis, on utilise curCustomer.
    */
   const canExceedQuota = useCallback((customerId?: string | null): boolean => {
      const state = usePrefStore.getState()
      const cust = customerId ?? state.curCustomer
      const r = computeRetailRoleForCustomer(cust, state.retailRole, state.isRetailManager, state.supervisedN1, state.supervisedN2)
      return r.isAdmin || r.isN1 || r.isN2
   }, [])

   /**
    * True si le user peut créer un KOT pour le customer donné (admin global,
    * OU N1 du customer). Si customerId est omis, on utilise curCustomer.
    */
   const canCreateKOT = useCallback((customerId?: string | null): boolean => {
      const state = usePrefStore.getState()
      const cust = customerId ?? state.curCustomer
      const r = computeRetailRoleForCustomer(cust, state.retailRole, state.isRetailManager, state.supervisedN1, state.supervisedN2)
      return r.isAdmin || r.isN1
   }, [])

   /**
    * Génère la contremarque KTM pré-remplie : QUOTAS_{type}_{YYYYMM}
    */
   const generateKTMMark = useCallback((clientType: KTMClientType): string => {
      return `QUOTAS_${clientType}_${moment().format('YYYYMM')}`
   }, [])

   /**
    * Extrait le type client (RET/ERE/INT) d'une contremarque KTM
    * QUOTAS_RET_202606 → 'RET'
    */
   const extractClientTypeFromKTMMark = useCallback((mark: string | null): KTMClientType | null => {
      if (!mark?.startsWith(KOT_MARK_PREFIX)) return null
      const parts = mark.split('_')
      // QUOTAS_RET_202606 → ['QUOTAS', 'RET', '202606']
      if (parts.length !== 3) return null
      const type = parts[1] as KTMClientType
      if (type === 'RET' || type === 'ERE' || type === 'INT') return type
      return null
   }, [])

   /**
    * Extrait la période YYYYMM d'une contremarque KTM
    * QUOTAS_IRE_202606 → '202606'
    */
   const extractPeriodFromKTMMark = useCallback((mark: string | null): string | null => {
      if (!mark?.startsWith(KOT_MARK_PREFIX)) return null
      const parts = mark.split('_')
      if (parts.length !== 3) return null
      const period = parts[2]
      if (period && /^\d{6}$/.test(period)) return period
      return null
   }, [])

   /**
    * True si le user peut créer un KTM. Les KTM sont des templates groupe
    * (concept global, déployés ensuite vers plusieurs customers) : on
    * autorise admin global, OU manager N1 d'au moins un customer.
    */
   const canCreateKTM = useCallback((): boolean => {
      const state = usePrefStore.getState()
      return state.retailRole === 'admin' || (state.supervisedN1?.length ?? 0) > 0
   }, [])

   return {
      generateKOTMark,
      extractPeriodFromMark,
      findCurrentKOT,
      findKOTByPeriod,
      getConsumedQuantities,
      getRetailQuota,
      getRemainingQtyForSku,
      getSkuRemaining,
      formatSkuRemaining,
      canExceedQuota,
      canCreateKOT,
      canCreateKTM,
      generateKTMMark,
      extractClientTypeFromKTMMark,
      extractPeriodFromKTMMark,
      KTM_CLIENT_TYPES,
      KOT_MARK_PREFIX,
   }
}

export default useRetailQuota
